import React, { useState, useEffect } from 'react';
import { recurringDeductionsApi, unitsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SmartNotes from '../components/SmartNotes';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  RefreshCw,
  DollarSign,
  Calendar,
  Truck,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, getWeekStart, sortUnitsForSelect } from '../lib/utils';

const DEDUCTION_TYPES = [
  { name: 'Fuel', type: 'standard' },
  { name: 'IFTA', type: 'standard' },
  { name: 'Insurance', type: 'standard' },
  { name: 'Parking', type: 'standard' },
  { name: 'Tolls', type: 'standard' },
  { name: 'ELD', type: 'standard' },
  { name: 'Repairs', type: 'standard' },
  { name: 'Advance', type: 'standard' },
  { name: 'Other', type: 'custom' },
];

export default function RecurringDeductions({ embedded = false }) {
  const [templates, setTemplates] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [applying, setApplying] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    deduction_type: 'standard',
    notes: '',
    // 'all'  → applies to every unit (legacy "all units" semantic)
    // 'pick' → user is choosing a subset; selection lives in `selectedUnitIds`
    unit_scope: 'all',
    selectedUnitIds: [],
    frequency: 'weekly',
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, unitsRes] = await Promise.all([
        recurringDeductionsApi.getAll(),
        unitsApi.getAll()
      ]);
      setTemplates(templatesRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.unit_scope === 'pick' && (!formData.selectedUnitIds || formData.selectedUnitIds.length === 0)) {
      toast.error('Pick at least one unit, or switch back to "All units"');
      return;
    }
    try {
      const data = {
        name: formData.name,
        amount: parseFloat(formData.amount),
        deduction_type: formData.deduction_type,
        notes: formData.notes,
        frequency: formData.frequency,
        is_active: formData.is_active,
        // unit_ids takes precedence; unit_id kept null for the new path.
        unit_id: null,
        unit_ids: formData.unit_scope === 'all' ? null : formData.selectedUnitIds,
      };

      if (editingTemplate) {
        await recurringDeductionsApi.update(editingTemplate.id, data);
        toast.success('Template updated successfully');
      } else {
        await recurringDeductionsApi.create(data);
        toast.success('Template created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    // Migrate legacy templates: if they only had unit_id (single), treat as 'pick' with one
    const legacyIds = template.unit_ids && template.unit_ids.length > 0
      ? template.unit_ids
      : (template.unit_id ? [template.unit_id] : []);
    setFormData({
      name: template.name,
      amount: template.amount.toString(),
      deduction_type: template.deduction_type,
      notes: template.notes || '',
      unit_scope: legacyIds.length === 0 ? 'all' : 'pick',
      selectedUnitIds: legacyIds,
      frequency: template.frequency,
      is_active: template.is_active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await recurringDeductionsApi.delete(id);
      toast.success('Template deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await recurringDeductionsApi.update(template.id, {
        ...template,
        is_active: !template.is_active,
      });
      loadData();
      toast.success(`Template ${template.is_active ? 'disabled' : 'enabled'}`);
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleApplyRecurring = async () => {
    setApplying(true);
    try {
      const weekStart = getWeekStart();
      const res = await recurringDeductionsApi.apply(weekStart);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply templates');
    } finally {
      setApplying(false);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      amount: '',
      deduction_type: 'standard',
      notes: '',
      unit_scope: 'all',
      selectedUnitIds: [],
      frequency: 'weekly',
      is_active: true,
    });
  };

  const activeTemplates = templates.filter(t => t.is_active);
  const inactiveTemplates = templates.filter(t => !t.is_active);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          {!embedded && (
            <>
              <h1 className="text-2xl font-bold text-foreground">Recurring Deductions</h1>
              <p className="text-muted-foreground">Manage automatic weekly deductions for your units</p>
            </>
          )}
          {embedded && (
            <p className="text-sm text-muted-foreground">
              Templates auto-apply each week. Edit or pause them anytime.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleApplyRecurring}
            disabled={applying || activeTemplates.length === 0}
            data-testid="apply-recurring-btn"
          >
            {applying ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Apply to This Week
          </Button>
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-template-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Template
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
        <CardContent className="p-6">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-sm text-muted-foreground">Active Templates</p>
              <p className="text-2xl font-bold text-blue-600">{activeTemplates.length}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Weekly Total</p>
              <p className="text-2xl font-bold text-red-600">
                -{formatCurrency(activeTemplates.reduce((sum, t) => {
                  const unitCount = t.unit_id ? 1 : units.length;
                  return sum + (t.amount * unitCount);
                }, 0))}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Applies To</p>
              <p className="text-2xl font-bold text-foreground">
                {activeTemplates.some(t => !t.unit_id) ? 'All Units' : `${new Set(activeTemplates.map(t => t.unit_id)).size} Unit(s)`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Deduction Templates
          </CardTitle>
          <CardDescription>
            These deductions will be automatically applied each week when you click "Apply to This Week"
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No recurring deduction templates yet</p>
              <p className="text-sm">Create a template to automate weekly deductions</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Active</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id} className={!template.is_active ? 'opacity-50' : ''}>
                    <TableCell>
                      <Switch
                        checked={template.is_active}
                        onCheckedChange={() => handleToggleActive(template)}
                        data-testid={`toggle-${template.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell className="font-mono text-red-600">
                      -{formatCurrency(template.amount)}
                    </TableCell>
                    <TableCell>
                      {/* Show All Units, the unit number(s) it applies to, or a count badge */}
                      {(() => {
                        const ids = (template.unit_ids && template.unit_ids.length > 0)
                          ? template.unit_ids
                          : (template.unit_id ? [template.unit_id] : []);
                        if (ids.length === 0) {
                          return <Badge variant="secondary">All Units</Badge>;
                        }
                        const nums = ids
                          .map(id => units.find(u => u.id === id)?.unit_number)
                          .filter(Boolean);
                        if (nums.length === 1) {
                          return (
                            <Badge variant="outline">
                              <Truck className="h-3 w-3 mr-1" />
                              {nums[0]}
                            </Badge>
                          );
                        }
                        return (
                          <Badge
                            variant="outline"
                            title={nums.join(', ')}
                          >
                            <Truck className="h-3 w-3 mr-1" />
                            {nums.length} units
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        <Calendar className="h-3 w-3 mr-1" />
                        {template.frequency}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-muted-foreground">
                      {template.notes || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(template)}
                          data-testid={`edit-${template.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-${template.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'New Recurring Deduction'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate 
                ? 'Update the recurring deduction template'
                : 'Create a template that will be applied automatically each week'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Deduction Name *</Label>
                <Select
                  value={formData.name}
                  onValueChange={(value) => {
                    const type = DEDUCTION_TYPES.find(d => d.name === value);
                    setFormData({ 
                      ...formData, 
                      name: value,
                      deduction_type: type?.type || 'custom'
                    });
                  }}
                >
                  <SelectTrigger data-testid="template-name-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEDUCTION_TYPES.map(type => (
                      <SelectItem key={type.name} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="template-amount-input"
                />
              </div>
            </div>

            {/* Apply-to selector — supports "All units" OR multi-select pick list */}
            <div className="space-y-2">
              <Label>Apply To</Label>
              <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                {/* All-units shortcut */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recur-unit-scope"
                    value="all"
                    checked={formData.unit_scope === 'all'}
                    onChange={() => setFormData({ ...formData, unit_scope: 'all', selectedUnitIds: [] })}
                    data-testid="template-scope-all"
                  />
                  <span className="text-sm font-medium">All units</span>
                  <span className="text-xs text-muted-foreground">— applies to every unit in the company</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recur-unit-scope"
                    value="pick"
                    checked={formData.unit_scope === 'pick'}
                    onChange={() => setFormData({ ...formData, unit_scope: 'pick' })}
                    data-testid="template-scope-pick"
                  />
                  <span className="text-sm font-medium">Specific units</span>
                  <span className="text-xs text-muted-foreground">— choose which units this applies to</span>
                </label>

                {formData.unit_scope === 'pick' && (
                  <div className="ml-6 mt-2 max-h-44 overflow-y-auto rounded border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                    {units.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2">No units yet — add a unit first.</p>
                    ) : sortUnitsForSelect(units).map((u) => {
                      const checked = formData.selectedUnitIds.includes(u.id);
                      const isInactive = (u.status || 'active') !== 'active';
                      return (
                        <label
                          key={u.id}
                          className={`flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer text-sm ${isInactive ? 'opacity-60 italic' : ''}`}
                          data-testid={`template-unit-checkbox-${u.id}`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...formData.selectedUnitIds, u.id]
                                : formData.selectedUnitIds.filter(id => id !== u.id);
                              setFormData({ ...formData, selectedUnitIds: next });
                            }}
                          />
                          <span className="font-mono text-xs text-slate-500">{u.unit_number}</span>
                          {isInactive && (
                            <span className="text-[10px] text-amber-600 ml-auto">inactive</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 max-w-xs">
              <Label htmlFor="frequency">Frequency</Label>
              <Select
                value={formData.frequency}
                onValueChange={(value) => setFormData({ ...formData, frequency: value })}
              >
                <SelectTrigger data-testid="template-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <SmartNotes
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes... (Math: 100+200)"
                rows={2}
                data-testid="template-notes-input"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Active (will be applied when you click "Apply to This Week")</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="template-submit-btn">
                {editingTemplate ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
